import { ArrowLeft } from "lucide-react";
import { Fragment } from "react";
import { ArcLink } from "../components/arc/lib/arc-provider";
import { Tooltip } from "../components/arc/tooltip/tooltip";
import { Icon } from "../icons/icon";
import type { NavItem, NavSection } from "./nav";
import styles from "./shell.module.css";

/** A nav item's icon: a lucide component (the console), or a Material Symbols name (the PWAs). */
export function NavIcon({ icon, size = 18 }: { icon: NavItem["icon"]; size?: number }) {
  if (typeof icon === "string") return <Icon name={icon} size={size} />;
  const Lucide = icon;
  return <Lucide size={size} aria-hidden="true" />;
}

/** A sidebar link: icon, label and an optional count; the current page is marked for screen readers too. */
export function NavLink({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <ArcLink href={item.href} className={styles.item} aria-current={active ? "page" : undefined}>
      <NavIcon icon={item.icon} />
      <span className={styles.itemLabel}>{item.label}</span>
      {item.count ? <span className={styles.count}>{item.count}</span> : null}
    </ArcLink>
  );
}

/** The expanded sidebar's groups (Sidebar / Expanded in the design). */
export function NavSections({
  sections,
  activeId,
  label,
}: {
  sections: NavSection[];
  activeId?: string;
  label: string;
}) {
  return (
    <nav className={styles.nav} aria-label={label}>
      {sections.map((section) => (
        <div key={section.id} className={styles.section}>
          {section.label ? <div className={styles.sectionLabel}>{section.label}</div> : null}
          {section.items.map((item) => (
            <NavLink key={item.id} item={item} active={item.id === activeId} />
          ))}
        </div>
      ))}
    </nav>
  );
}

/** A rail icon with its label in a tooltip (Rail Item in the design). */
export function RailLink({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <Tooltip content={item.label} side="top">
      <ArcLink
        href={item.href}
        className={styles.railItem}
        aria-current={active ? "page" : undefined}
        aria-label={item.label}
      >
        <NavIcon icon={item.icon} />
      </ArcLink>
    </Tooltip>
  );
}

/** The collapsed sidebar's icons, a divider between groups (Sidebar / Collapsed). */
export function RailSections({
  sections,
  activeId,
  label,
}: {
  sections: NavSection[];
  activeId?: string;
  label: string;
}) {
  return (
    <nav className={styles.railNav} aria-label={label}>
      {sections.map((section, index) => (
        <Fragment key={section.id}>
          {index > 0 ? <span className={styles.railDivider} aria-hidden="true" /> : null}
          {section.items.map((item) => (
            <RailLink key={item.id} item={item} active={item.id === activeId} />
          ))}
        </Fragment>
      ))}
    </nav>
  );
}

/** The top of a sub-menu's sidebar (Settings Header, `BH45D`): a way back and the area's title. */
export function SidebarBack({
  href,
  label,
  title,
  compact = false,
}: {
  href: string;
  label: string;
  title: string;
  compact?: boolean;
}) {
  const back: NavItem = { id: "back", label, href, icon: ArrowLeft };
  if (compact) return <RailLink item={back} active={false} />;
  return (
    <div className={styles.backHeader}>
      <NavLink item={back} active={false} />
      <p className={styles.backTitle}>{title}</p>
    </div>
  );
}
